import React, { useMemo, useState } from 'react';

import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { InlineFormControl } from '../../../common/inline/InlineFormControl';
import SearchFilterButton from '../../../common/SearchFilterButton';
import SearchFilterDrawer, {
  BaseSearchFilterProps
} from '../../../common/SearchFilterDrawer';
import { useFindGroups } from '../../groups/Group.hooks';

export const UserStatusFilters = ['all', 'active', 'inactive'] as const;

export type UserStatusFilter = (typeof UserStatusFilters)[number];

export interface UserListFilterOptions {
  groupIds: number[];
  status?: UserStatusFilter;
}

export interface UserListFilterProps
  extends BaseSearchFilterProps<UserListFilterOptions> {
  options?: Partial<UserListFilterOptions>;
}

function createInitialOptions(
  options?: Partial<UserListFilterOptions>
): UserListFilterOptions {
  return { groupIds: [], status: undefined, ...options };
}

export function isValidUserStatus(status?: string): status is UserStatusFilter {
  return (UserStatusFilters as any).includes(status);
}

const UserListFilter: React.FC<UserListFilterProps> = (
  props: UserListFilterProps
) => {
  const { onFilter, options: initialOptions } = props;
  const { groupList } = useFindGroups(true);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(() =>
    createInitialOptions(initialOptions)
  );

  const selected = useMemo(() => {
    if (!groupList) return [];
    const selected = groupList.data.filter(option =>
      options.groupIds.includes(option.id)
    );
    return selected;
  }, [options.groupIds, groupList]);

  return (
    <>
      <SearchFilterButton onClick={() => setOpen(true)} />
      <SearchFilterDrawer
        open={open}
        title='Filter Users'
        onClose={() => setOpen(false)}
        onClear={() => setOptions(createInitialOptions())}
        onFilter={() => {
          setOpen(false);
          onFilter?.(options);
        }}
      >
        <InlineFormControl label='Team'>
          <Autocomplete
            multiple
            options={groupList?.data || []}
            value={selected}
            getOptionLabel={option => option.name}
            onChange={(_, value) =>
              setOptions({ ...options, groupIds: value.map(group => group.id) })
            }
            renderInput={params => (
              <TextField
                {...params}
                fullWidth
                variant='outlined'
                placeholder='Select Team'
              />
            )}
          />
        </InlineFormControl>
        <InlineFormControl label='Status'>
          <RadioGroup
            row
            value={options.status || ''}
            onChange={(_, value) => {
              if (isValidUserStatus(value)) {
                setOptions({ ...options, status: value });
              }
            }}
          >
            {UserStatusFilters.map((status, i) => (
              <FormControlLabel
                key={i}
                style={{ textTransform: 'capitalize' }}
                label={status}
                value={status}
                control={<Radio color='primary' />}
              />
            ))}
          </RadioGroup>
        </InlineFormControl>
      </SearchFilterDrawer>
    </>
  );
};

export default UserListFilter;
